import { useRef, useState } from "react";
import { Upload, FileText, ChevronDown } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Input } from "../../../../components/ui/input";
import { getFileInputAcceptString } from "../../../../lib/fileTypes";
import { fetchFastApi } from "../../../../api/fastapi";
import { UploadSupplementaryDocumentsResponseSchema } from "../../../../models/audit/sessionSupplementaryDocument";
import { toast } from "sonner";
import { validateFilesWithToast } from "../../../../lib/fileUploadValidation";
import { matchingSessionApiBase } from "../../../../lib/sessionApiBase";
import type { SessionKind } from "../../../../hooks/audit/useControlSessionEnvelope";

interface SupplementaryDocumentsDropdownProps {
  /** The control session or work step session to attach the uploads to. */
  sessionId: string;
  sessionKind: SessionKind;
  onUploadComplete?: () => void;
  onViewDocuments: () => void;
}

export const SupplementaryDocumentsDropdown = ({
  sessionId,
  sessionKind,
  onUploadComplete,
  onViewDocuments,
}: SupplementaryDocumentsDropdownProps) => {
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    const validFiles = await validateFilesWithToast(newFiles);
    if (validFiles.length === 0) return;

    setUploading(true);

    try {
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetchFastApi({
        method: "POST",
        path: `${matchingSessionApiBase(sessionKind, sessionId)}/upload-supplementary-documents`,
        body: formData,
      });

      const parsed = UploadSupplementaryDocumentsResponseSchema.parse(response);

      toast.success(parsed.message || "Documents uploaded successfully");

      onUploadComplete?.();
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error("Failed to upload documents");
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpload(e.target.files);
    setOpen(false);
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
    setOpen(false);
  };

  const handleViewClick = () => {
    onViewDocuments();
    setOpen(false);
  };

  return (
    <div className="relative">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={uploading}
            loading={uploading}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Additional Documents
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleUploadClick}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Documents
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleViewClick}>
            <FileText className="h-4 w-4 mr-2" />
            View Documents
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Input
        ref={inputRef}
        type="file"
        accept={getFileInputAcceptString()}
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />
    </div>
  );
};
